import React, { useState } from 'react';
import styled from 'styled-components';
import { Fade } from 'react-awesome-reveal';

// --- STYLED COMPONENTS ---

const CardContainer = styled.div`
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  max-width: 820px;
  margin: 40px auto;
  padding: 45px;
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);

  @media (max-width: 640px) {
    padding: 25px 20px;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  color: #0c2340; /* Corporate dark navy */
  margin: 0 0 12px 0;
`;

const RequiredNote = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  margin: 0 0 25px 0;

  span {
    color: #ef4444; /* Red asterisk */
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TextareaWrapper = styled.div`
  width: 100%;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 220px;
  padding: 15px;
  font-size: 0.95rem;
  color: #334155;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #439E6D; /* Your signature brand color */
    box-shadow: 0 0 0 3px rgba(67, 158, 109, 0.15);
  }
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 0.95rem;
  color: #334155;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #439E6D; /* Your signature brand color */
    box-shadow: 0 0 0 3px rgba(67, 158, 109, 0.15);
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  color: #334155;
  cursor: pointer;
  user-select: none;
  margin-top: 5px;
  line-height: 1.4;
`;

const StyledCheckbox = styled.input`
  margin-top: 3px;
  cursor: pointer;
  accent-color: #439E6D; /* Keeps standard box matching brand green */
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  background-color: #439E6D; /* Standard high-visibility action blue matching image */
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 12px 28px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #0052a3;
  }

  &:active {
    background-color: #004080;
  }
`;

// --- MAIN COMPONENT ---

const LeaveCommentForm = () => {
  const [formData, setFormData] = useState({
    comment: '',
    name: '',
    email: '',
    website: '',
    saveInfo: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Context hook logic ready to plug into your endpoint state action here
    console.log('Submitted Comment Context:', formData);
  };

  return (
    <CardContainer>
      <Fade triggerOnce direction="up" duration={700}>
        
        <FormTitle>Leave a Comment</FormTitle>
        <RequiredNote>
          Your email address will not be published. Required fields are marked <span>*</span>
        </RequiredNote>

        <StyledForm onSubmit={handleSubmit}>
          {/* Big Comment Textarea */}
          <TextareaWrapper>
            <StyledTextarea
              name="comment"
              placeholder="Type here.."
              value={formData.comment}
              onChange={handleChange}
              required
            />
          </TextareaWrapper>

          {/* 3-Column Inputs Grid */}
          <InputRow>
            <StyledInput
              type="text"
              name="name"
              placeholder="Name*"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <StyledInput
              type="email"
              name="email"
              placeholder="Email*"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <StyledInput
              type="url"
              name="website"
              placeholder="Website"
              value={formData.website}
              onChange={handleChange}
            />
          </InputRow>

          {/* Checkbox Permission Choice */}
          <CheckboxContainer>
            <StyledCheckbox
              type="checkbox"
              name="saveInfo"
              checked={formData.saveInfo}
              onChange={handleChange}
            />
            Save my name, email, and website in this browser for the next time I comment.
          </CheckboxContainer>

          {/* Action Trigger */}
          <SubmitButton type="submit">Post Comment</SubmitButton>
          
        </StyledForm>
      </Fade>
    </CardContainer>
  );
};

export default LeaveCommentForm;